/**
 * Schedule API - CRUD operations for scheduled collection assignments and removals
 */

import { supabase, getShopDomain } from './supabase';
import { getToken, getSessionTokenForBackend, isEmbedded } from './authenticated-fetch';
import type {
  ScheduledCollectionAssignment,
  ScheduledCollectionRemoval,
  ScheduledDiscount,
  ScheduledThemeChange,
  ThemeFileChange,
  UnifiedSchedule,
  ScheduleStatus,
  FilterState,
  ActivityLog,
  PreviewSession,
  PreviewRequest,
} from '../types';
import type {
  VisualSectionSchedule,
  ScheduledSectionChange,
  CollectionTemplateAssignmentData,
} from '../types/section-scheduler';

// Use dynamic shop domain from the app context (supports multi-store)
const getShopDomainForAPI = () => getShopDomain();

const normalizeSearchQuery = (value: string): string =>
  value
    .trim()
    .slice(0, 80)
    .replace(/[%,_(),]/g, ' ')
    .replace(/\s+/g, ' ');

const buildSearchPattern = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizeSearchQuery(value);
  if (!normalized) {
    return null;
  }

  return `%${normalized}%`;
};

// ============================================================================
// COLLECTION ASSIGNMENT SCHEDULES
// ============================================================================

export async function createCollectionAssignment(
  data: Omit<ScheduledCollectionAssignment, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'shopDomain' | 'retryCount'>
): Promise<ScheduledCollectionAssignment> {
  const { data: result, error } = await supabase
    .from('scheduled_collection_assignments')
    .insert({
      shop_domain: getShopDomainForAPI(),
      product_id: data.productId,
      product_title: data.productTitle,
      product_handle: data.productHandle,
      collection_id: data.collectionId,
      collection_title: data.collectionTitle,
      collection_handle: data.collectionHandle,
      scheduled_at: data.scheduledAt,
      timezone: data.timezone,
      created_by: data.createdBy,
      notes: data.notes,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create assignment: ${error.message}`);
  
  // Log activity
  await logActivity('schedule_created', 'assignment', result.id, {
    product_title: data.productTitle,
    collection_title: data.collectionTitle,
    scheduled_at: data.scheduledAt,
  });

  return mapAssignmentFromDB(result);
}

export async function getCollectionAssignments(
  filters?: FilterState
): Promise<ScheduledCollectionAssignment[]> {
  let query = supabase
    .from('scheduled_collection_assignments')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .order('scheduled_at', { ascending: true });

  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters?.dateRange) {
    query = query
      .gte('scheduled_at', filters.dateRange.start.toISOString())
      .lte('scheduled_at', filters.dateRange.end.toISOString());
  }

  if (filters?.collectionId) {
    query = query.eq('collection_id', filters.collectionId);
  }

  const searchPattern = buildSearchPattern(filters?.searchQuery);
  if (searchPattern) {
    query = query.or(
      `product_title.ilike.${searchPattern},collection_title.ilike.${searchPattern}`
    );
  }

  const { data, error } = await query;

  if (error) throw new Error(`Failed to fetch assignments: ${error.message}`);
  return (data || []).map(mapAssignmentFromDB);
}

export async function updateCollectionAssignment(
  id: string,
  updates: Partial<ScheduledCollectionAssignment>
): Promise<ScheduledCollectionAssignment> {
  const updateData: Record<string, any> = {};
  
  if (updates.scheduledAt) updateData.scheduled_at = updates.scheduledAt;
  if (updates.timezone) updateData.timezone = updates.timezone;
  if (updates.status) updateData.status = updates.status;
  if (updates.notes !== undefined) updateData.notes = updates.notes;
  if (updates.errorMessage) updateData.error_message = updates.errorMessage;
  if (updates.processedAt) updateData.processed_at = updates.processedAt;

  const { data, error } = await supabase
    .from('scheduled_collection_assignments')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update assignment: ${error.message}`);
  
  await logActivity('schedule_updated', 'assignment', id, { updates });

  return mapAssignmentFromDB(data);
}

export async function deleteCollectionAssignment(id: string): Promise<void> {
  const { error } = await supabase
    .from('scheduled_collection_assignments')
    .delete()
    .eq('id', id);

  if (error) throw new Error(`Failed to delete assignment: ${error.message}`);
  
  await logActivity('schedule_cancelled', 'assignment', id);
}

export async function cancelCollectionAssignment(id: string): Promise<ScheduledCollectionAssignment> {
  // Fetch existing row to validate visibility and shop domain (help debug RLS/env issues)
  const { data: existing, error: fetchError } = await supabase
    .from('scheduled_collection_assignments')
    .select('id, shop_domain, status')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    const msg = fetchError?.message || 'Schedule not found or permission denied (fetch)';
    throw new Error(msg);
  }

  if (existing.shop_domain !== getShopDomainForAPI()) {
    throw new Error(`Shop domain mismatch when cancelling schedule. Client=${getShopDomainForAPI()} DB=${existing.shop_domain}`);
  }

  return updateCollectionAssignment(id, { status: 'cancelled' });
}

/**
 * Execute a collection assignment immediately (add product to collection)
 */
export async function executeCollectionAssignment(id: string): Promise<ScheduledCollectionAssignment> {
  // First get the assignment details
  const { data: assignment, error: fetchError } = await supabase
    .from('scheduled_collection_assignments')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError || !assignment) {
    throw new Error(`Failed to fetch assignment: ${fetchError?.message || 'Not found'}`);
  }

  // Import the Shopify API function dynamically to avoid circular imports
  const { addProductsToCollection } = await import('./shopify-api');

  try {
    // Execute the Shopify API call
    await addProductsToCollection(assignment.collection_id, [assignment.product_id]);

    // Update the assignment as completed
    const updated = await updateCollectionAssignment(id, {
      status: 'completed',
      processedAt: new Date().toISOString(),
    });

    await logActivity('schedule_executed', 'assignment', id, {
      product_title: assignment.product_title,
      collection_title: assignment.collection_title,
      result: 'success',
    });

    return updated;
  } catch (error) {
    // Update the assignment as failed
    await updateCollectionAssignment(id, {
      status: 'failed',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    });

    await logActivity('schedule_failed', 'assignment', id, {
      product_title: assignment.product_title,
      collection_title: assignment.collection_title,
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    throw error;
  }
}

// ============================================================================
// COLLECTION REMOVAL SCHEDULES
// ============================================================================

export async function createCollectionRemoval(
  data: Omit<ScheduledCollectionRemoval, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'shopDomain' | 'retryCount'>
): Promise<ScheduledCollectionRemoval> {
  const { data: result, error } = await supabase
    .from('scheduled_collection_removals')
    .insert({
      shop_domain: getShopDomainForAPI(),
      product_id: data.productId,
      product_title: data.productTitle,
      product_handle: data.productHandle,
      collection_id: data.collectionId,
      collection_title: data.collectionTitle,
      collection_handle: data.collectionHandle,
      scheduled_at: data.scheduledAt,
      timezone: data.timezone,
      created_by: data.createdBy,
      notes: data.notes,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create removal: ${error.message}`);
  
  await logActivity('schedule_created', 'removal', result.id, {
    product_title: data.productTitle,
    collection_title: data.collectionTitle,
    scheduled_at: data.scheduledAt,
  });

  return mapRemovalFromDB(result);
}

export async function getCollectionRemovals(
  filters?: FilterState
): Promise<ScheduledCollectionRemoval[]> {
  let query = supabase
    .from('scheduled_collection_removals')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .order('scheduled_at', { ascending: true });

  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters?.dateRange) {
    query = query
      .gte('scheduled_at', filters.dateRange.start.toISOString())
      .lte('scheduled_at', filters.dateRange.end.toISOString());
  }

  if (filters?.collectionId) {
    query = query.eq('collection_id', filters.collectionId);
  }

  const searchPattern = buildSearchPattern(filters?.searchQuery);
  if (searchPattern) {
    query = query.or(
      `product_title.ilike.${searchPattern},collection_title.ilike.${searchPattern}`
    );
  }

  const { data, error } = await query;

  if (error) throw new Error(`Failed to fetch removals: ${error.message}`);
  return (data || []).map(mapRemovalFromDB);
}

export async function updateCollectionRemoval(
  id: string,
  updates: Partial<ScheduledCollectionRemoval>
): Promise<ScheduledCollectionRemoval> {
  const updateData: Record<string, any> = {};
  
  if (updates.scheduledAt) updateData.scheduled_at = updates.scheduledAt;
  if (updates.timezone) updateData.timezone = updates.timezone;
  if (updates.status) updateData.status = updates.status;
  if (updates.notes !== undefined) updateData.notes = updates.notes;

  const { data, error } = await supabase
    .from('scheduled_collection_removals')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update removal: ${error.message}`);

  return mapRemovalFromDB(data);
}

export async function deleteCollectionRemoval(id: string): Promise<void> {
  const { error } = await supabase
    .from('scheduled_collection_removals')
    .delete()
    .eq('id', id);

  if (error) throw new Error(`Failed to delete removal: ${error.message}`);
}

export async function cancelCollectionRemoval(id: string): Promise<ScheduledCollectionRemoval> {
  // Fetch existing row to validate visibility and shop domain (help debug RLS/env issues)
  const { data: existing, error: fetchError } = await supabase
    .from('scheduled_collection_removals')
    .select('id, shop_domain, status')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    const msg = fetchError?.message || 'Schedule not found or permission denied (fetch)';
    throw new Error(msg);
  }

  if (existing.shop_domain !== getShopDomainForAPI()) {
    throw new Error(`Shop domain mismatch when cancelling schedule. Client=${getShopDomainForAPI()} DB=${existing.shop_domain}`);
  }

  return updateCollectionRemoval(id, { status: 'cancelled' });
}

/**
 * Execute a collection removal immediately (remove product from collection)
 */
export async function executeCollectionRemoval(id: string): Promise<ScheduledCollectionRemoval> {
  // First get the removal details
  const { data: removal, error: fetchError } = await supabase
    .from('scheduled_collection_removals')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError || !removal) {
    throw new Error(`Failed to fetch removal: ${fetchError?.message || 'Not found'}`);
  }

  // Import the Shopify API function dynamically to avoid circular imports
  const { removeProductsFromCollection } = await import('./shopify-api');

  try {
    // Execute the Shopify API call
    await removeProductsFromCollection(removal.collection_id, [removal.product_id]);

    // Update the removal as completed
    const updated = await updateCollectionRemoval(id, {
      status: 'completed',
      processedAt: new Date().toISOString(),
    });

    await logActivity('schedule_executed', 'removal', id, {
      product_title: removal.product_title,
      collection_title: removal.collection_title,
      result: 'success',
    });

    return updated;
  } catch (error) {
    // Update the removal as failed
    await updateCollectionRemoval(id, {
      status: 'failed',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    });

    await logActivity('schedule_failed', 'removal', id, {
      product_title: removal.product_title,
      collection_title: removal.collection_title,
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    throw error;
  }
}

// ============================================================================
// DISCOUNT SCHEDULES
// ============================================================================

export async function createDiscountSchedule(
  data: Omit<ScheduledDiscount, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'shopDomain' | 'retryCount'>
): Promise<ScheduledDiscount> {
  const { data: result, error } = await supabase
    .from('scheduled_discounts')
    .insert({
      shop_domain: getShopDomainForAPI(),
      discount_id: data.discountId,
      discount_title: data.discountTitle,
      discount_code: data.discountCode,
      discount_type: data.discountType,
      action: data.action,
      scheduled_at: data.scheduledAt,
      timezone: data.timezone,
      created_by: data.createdBy,
      notes: data.notes,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create discount schedule: ${error.message}`);
  
  await logActivity('schedule_created', 'discount', result.id, {
    discount_title: data.discountTitle,
    action: data.action,
    scheduled_at: data.scheduledAt,
  });

  return mapDiscountFromDB(result);
}

export async function getDiscountSchedules(
  filters?: FilterState
): Promise<ScheduledDiscount[]> {
  let query = supabase
    .from('scheduled_discounts')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .order('scheduled_at', { ascending: true });

  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  const { data, error } = await query;

  if (error) throw new Error(`Failed to fetch discount schedules: ${error.message}`);
  return (data || []).map(mapDiscountFromDB);
}

// ============================================================================
// UNIFIED SCHEDULES
// ============================================================================

export async function getAllSchedules(filters?: FilterState): Promise<UnifiedSchedule[]> {
  const [assignments, removals, discounts, sectionSchedules] = await Promise.all([
    getCollectionAssignments(filters),
    getCollectionRemovals(filters),
    getDiscountSchedules(filters),
    getSectionSchedules(filters),
  ]);

  const unified: UnifiedSchedule[] = [
    ...assignments.map((a): UnifiedSchedule => ({
      id: a.id,
      type: 'assignment',
      status: a.status,
      scheduledAt: a.scheduledAt,
      timezone: a.timezone,
      processedAt: a.processedAt,
      errorMessage: a.errorMessage,
      productId: a.productId,
      productTitle: a.productTitle,
      productHandle: a.productHandle,
      collectionId: a.collectionId,
      collectionTitle: a.collectionTitle,
      collectionHandle: a.collectionHandle,
      createdBy: a.createdBy,
      notes: a.notes,
      createdAt: a.createdAt,
    })),
    ...removals.map((r): UnifiedSchedule => ({
      id: r.id,
      type: 'removal',
      status: r.status,
      scheduledAt: r.scheduledAt,
      timezone: r.timezone,
      processedAt: r.processedAt,
      errorMessage: r.errorMessage,
      productId: r.productId,
      productTitle: r.productTitle,
      productHandle: r.productHandle,
      collectionId: r.collectionId,
      collectionTitle: r.collectionTitle,
      collectionHandle: r.collectionHandle,
      createdBy: r.createdBy,
      notes: r.notes,
      createdAt: r.createdAt,
    })),
    ...discounts.map((d): UnifiedSchedule => ({
      id: d.id,
      type: 'discount',
      status: d.status,
      scheduledAt: d.scheduledAt,
      timezone: d.timezone,
      processedAt: d.processedAt,
      errorMessage: d.errorMessage,
      discountId: d.discountId,
      discountTitle: d.discountTitle,
      discountAction: d.action,
      createdBy: d.createdBy,
      notes: d.notes,
      createdAt: d.createdAt,
    })),
    ...sectionSchedules.map((s): UnifiedSchedule => ({
      id: s.id,
      type: 'section',
      status: s.status,
      scheduledAt: s.scheduledAt,
      scheduledOffAt: s.scheduledOffAt,
      timezone: s.timezone,
      processedAt: s.processedAt,
      errorMessage: s.errorMessage,
      sectionTitle: s.title,
      templateName: s.templateName,
      sectionChangesCount: s.changes.length,
      sectionChanges: s.changes.map(c => ({
        sectionId: c.sectionId,
        sectionName: c.sectionName,
        action: c.action,
      })),
      isRevert: s.isRevert,
      revertScheduleId: s.revertScheduleId,
      createdBy: s.createdBy,
      notes: s.notes,
      createdAt: s.createdAt,
    })),
  ];

  // Sort by scheduled time (descending - newest first)
  unified.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());

  return unified;
}

export async function getScheduleStats(): Promise<{
  /** Started and not finished */
  active: number;
  /** Pending and scheduled between now and 24h from now */
  upcoming24h: number;
  /** Successfully executed */
  completed: number;
  /** Pending and scheduled in the past */
  overdue: number;
}> {
  const now = new Date();
  const next24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  // Use unified schedules so stats include section schedules as well.
  const schedules = await getAllSchedules();

  const isValidDate = (d: Date) => !Number.isNaN(d.getTime());

  return {
    active: schedules.filter(s => s.status === 'processing').length,
    completed: schedules.filter(s => s.status === 'completed').length,
    upcoming24h: schedules.filter(s => {
      if (s.status !== 'pending') return false;
      const scheduledAt = new Date(s.scheduledAt);
      if (!isValidDate(scheduledAt)) return false;
      return scheduledAt >= now && scheduledAt <= next24h;
    }).length,
    overdue: schedules.filter(s => {
      if (s.status !== 'pending') return false;
      const scheduledAt = new Date(s.scheduledAt);
      if (!isValidDate(scheduledAt)) return false;
      return scheduledAt < now;
    }).length,
  };
}

// ============================================================================
// PREVIEW SESSIONS
// ============================================================================

export async function createPreviewSession(request: PreviewRequest): Promise<PreviewSession> {
  const sessionToken = crypto.randomUUID();
  // Default preview session duration: 7 days (configurable via request)
  const DEFAULT_PREVIEW_DAYS = 7;
  let expiresAt: Date;
  if (request.expiresAt) {
    expiresAt = new Date(request.expiresAt);
  } else if (request.expiresInDays && Number.isFinite(request.expiresInDays)) {
    expiresAt = new Date(Date.now() + request.expiresInDays * 24 * 60 * 60 * 1000);
  } else {
    expiresAt = new Date(Date.now() + DEFAULT_PREVIEW_DAYS * 24 * 60 * 60 * 1000);
  }
  
  // Generate preview URL based on type using handle (for storefront URLs)
  let previewUrl = `https://${getShopDomainForAPI()}`;
  const pathSegment = request.targetHandle || request.targetId;
  if (request.targetType === 'product' && pathSegment) {
    previewUrl += `/products/${pathSegment}`;
  } else if (request.targetType === 'collection' && pathSegment) {
    previewUrl += `/collections/${pathSegment}`;
  }
  
  // Add preview token to URL for storefront password bypass
  previewUrl += `?preview_token=${sessionToken}`;

  const { data, error } = await supabase
    .from('preview_sessions')
    .insert({
      shop_domain: getShopDomainForAPI(),
      preview_type: request.type,
      preview_target_id: request.targetId,
      preview_target_type: request.targetType,
      schedule_id: request.scheduleId,
      preview_datetime: request.previewDatetime,
      session_token: sessionToken,
      preview_url: previewUrl,
      expires_at: expiresAt.toISOString(),
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create preview session: ${error.message}`);
  
  await logActivity('preview_generated', 'preview', data.id, {
    preview_type: request.type,
    target_id: request.targetId,
  });

  return mapPreviewSessionFromDB(data);
}

export async function getPreviewSessions(): Promise<PreviewSession[]> {
  const { data, error } = await supabase
    .from('preview_sessions')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch preview sessions: ${error.message}`);
  return (data || []).map(mapPreviewSessionFromDB);
}

// ============================================================================
// ACTIVITY LOGS
// ============================================================================

type ActivityActor = {
  type: 'user' | 'system';
  id?: string;
  email?: string;
  name?: string;
};

let cachedActivityActor: { shopDomain: string; actor: ActivityActor; expiresAt: number } | null = null;

function decodeJwtPayload(token: string): any | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    return JSON.parse(atob(part));
  } catch {
    return null;
  }
}

async function getActivityActor(shopDomain: string): Promise<ActivityActor | null> {
  const now = Date.now();
  if (cachedActivityActor && cachedActivityActor.shopDomain === shopDomain && cachedActivityActor.expiresAt > now) {
    return cachedActivityActor.actor;
  }

  if (!isEmbedded()) return null;

  const token = await getToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const actor: ActivityActor = {
    type: 'user',
    id: payload.sub || payload.dest?.split('/')?.pop(),
    email: payload.email,
    name: payload.name,
  };

  try {
    if (actor.id) {
      const { data, error } = await supabase
        .from('user_settings')
        .select('user_name')
        .eq('shop_domain', shopDomain)
        .eq('user_id', actor.id)
        .maybeSingle();

      if (!error && data?.user_name) {
        actor.name = data.user_name;
      }
    }
  } catch {
    // Ignore name lookup failures; still log with token-derived info.
  }

  cachedActivityActor = {
    shopDomain,
    actor,
    expiresAt: now + 60_000,
  };

  return actor;
}

export async function logActivity(
  action: string,
  entityType?: string,
  entityId?: string,
  details?: Record<string, any>
): Promise<void> {
  const shopDomain = getShopDomainForAPI();

  try {
    const actor = await getActivityActor(shopDomain);

    const mergedDetails = {
      ...(details || {}),
      ...(actor && !(details as any)?.actor ? { actor } : {}),
    };

    const { error } = await supabase.from('activity_logs').insert({
      shop_domain: shopDomain,
      action,
      entity_type: entityType,
      entity_id: entityId,
      details: Object.keys(mergedDetails).length > 0 ? mergedDetails : undefined,
      user_id: actor?.id,
      user_email: actor?.email,
    });

    if (error) {
      console.warn('[Activity] Failed to write activity log:', error);
    }
  } catch (error) {
    console.warn('[Activity] Failed to write activity log:', error);
  }
}

export async function getActivityLogs(limit = 50): Promise<ActivityLog[]> {
  const shopDomain = getShopDomainForAPI();
  const { data, error } = await supabase
    .from('activity_logs')
    .select('*')
    .or(`shop_domain.eq.${shopDomain},shop_domain.is.null`)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to fetch activity logs: ${error.message}`);
  return (data || []).map(mapActivityLogFromDB);
}

// ============================================================================
// MAPPERS
// ============================================================================

function mapAssignmentFromDB(row: any): ScheduledCollectionAssignment {
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    productId: row.product_id,
    productTitle: row.product_title,
    productHandle: row.product_handle,
    collectionId: row.collection_id,
    collectionTitle: row.collection_title,
    collectionHandle: row.collection_handle,
    scheduledAt: row.scheduled_at,
    timezone: row.timezone,
    status: row.status as ScheduleStatus,
    processedAt: row.processed_at,
    errorMessage: row.error_message,
    retryCount: row.retry_count,
    createdBy: row.created_by,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapRemovalFromDB(row: any): ScheduledCollectionRemoval {
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    productId: row.product_id,
    productTitle: row.product_title,
    productHandle: row.product_handle,
    collectionId: row.collection_id,
    collectionTitle: row.collection_title,
    collectionHandle: row.collection_handle,
    scheduledAt: row.scheduled_at,
    timezone: row.timezone,
    status: row.status as ScheduleStatus,
    processedAt: row.processed_at,
    errorMessage: row.error_message,
    retryCount: row.retry_count,
    createdBy: row.created_by,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapDiscountFromDB(row: any): ScheduledDiscount {
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    discountId: row.discount_id,
    discountTitle: row.discount_title,
    discountCode: row.discount_code,
    discountType: row.discount_type,
    action: row.action,
    scheduledAt: row.scheduled_at,
    timezone: row.timezone,
    status: row.status as ScheduleStatus,
    processedAt: row.processed_at,
    errorMessage: row.error_message,
    retryCount: row.retry_count,
    createdBy: row.created_by,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapPreviewSessionFromDB(row: any): PreviewSession {
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    previewType: row.preview_type,
    previewTargetId: row.preview_target_id,
    previewTargetType: row.preview_target_type,
    scheduleId: row.schedule_id,
    scheduleType: row.schedule_type,
    previewDatetime: row.preview_datetime,
    previewUrl: row.preview_url,
    expiresAt: row.expires_at,
    accessedCount: row.accessed_count,
    lastAccessedAt: row.last_accessed_at,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

function mapActivityLogFromDB(row: any): ActivityLog {
  const actorName = row?.details?.actor?.name;
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    details: row.details,
    userId: row.user_id,
    userEmail: row.user_email,
    userName: actorName,
    createdAt: row.created_at,
  };
}

// ============================================================================
// THEME SCHEDULE FUNCTIONS
// ============================================================================

export interface CreateThemeScheduleData {
  targetMode: 'main' | 'specific' | 'all_unpublished' | 'main_and_unpublished';
  targetThemeIds?: string[];  // Only used when targetMode='specific'
  title: string;
  description?: string;
  changes: ThemeFileChange[];
  scheduledAt: string;
  timezone: string;
  createdBy?: string;
  notes?: string;
}

/**
 * Create a scheduled theme change with file modifications
 * Theme-agnostic: targets themes based on targetMode at execution time
 */
export async function createThemeSchedule(
  data: CreateThemeScheduleData
): Promise<ScheduledThemeChange> {
  // First, create the main schedule record
  const { data: scheduleResult, error: scheduleError } = await supabase
    .from('scheduled_theme_changes')
    .insert({
      shop_domain: getShopDomainForAPI(),
      target_mode: data.targetMode,
      target_theme_ids: data.targetThemeIds || null,
      title: data.title,
      description: data.description,
      scheduled_at: data.scheduledAt,
      timezone: data.timezone,
      created_by: data.createdBy,
      notes: data.notes,
      status: 'pending',
    })
    .select()
    .single();

  if (scheduleError) throw new Error(`Failed to create theme schedule: ${scheduleError.message}`);

  // Then, create the file change records
  if (data.changes.length > 0) {
    const fileChanges = data.changes.map(change => ({
      schedule_id: scheduleResult.id,
      filename: change.filename,
      file_type: change.fileType,
      current_content: change.currentContent,
      new_content: change.newContent,
      action: change.action,
    }));

    const { error: filesError } = await supabase
      .from('theme_file_changes')
      .insert(fileChanges);

    if (filesError) {
      // Rollback: delete the schedule if file changes failed
      await supabase.from('scheduled_theme_changes').delete().eq('id', scheduleResult.id);
      throw new Error(`Failed to create file changes: ${filesError.message}`);
    }
  }

  // Log activity
  await logActivity('schedule_created', 'theme', scheduleResult.id, {
    target_mode: data.targetMode,
    title: data.title,
    files_count: data.changes.length,
    scheduled_at: data.scheduledAt,
  });

  return mapThemeScheduleFromDB(scheduleResult, data.changes);
}

/**
 * Get all theme schedules with optional filters
 */
export async function getThemeSchedules(
  filters?: FilterState
): Promise<ScheduledThemeChange[]> {
  let query = supabase
    .from('scheduled_theme_changes')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .order('scheduled_at', { ascending: true });

  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters?.dateRange) {
    query = query
      .gte('scheduled_at', filters.dateRange.start.toISOString())
      .lte('scheduled_at', filters.dateRange.end.toISOString());
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch theme schedules: ${error.message}`);

  // Fetch file changes for each schedule
  const schedules = await Promise.all(
    (data || []).map(async (row) => {
      const { data: fileChanges } = await supabase
        .from('theme_file_changes')
        .select('*')
        .eq('schedule_id', row.id);

      const changes: ThemeFileChange[] = (fileChanges || []).map(fc => ({
        filename: fc.filename,
        fileType: fc.file_type,
        currentContent: fc.current_content,
        newContent: fc.new_content,
        action: fc.action,
      }));

      return mapThemeScheduleFromDB(row, changes);
    })
  );

  return schedules;
}

/**
 * Get a single theme schedule by ID
 */
export async function getThemeSchedule(id: string): Promise<ScheduledThemeChange | null> {
  const { data: row, error } = await supabase
    .from('scheduled_theme_changes')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !row) return null;

  // Fetch file changes
  const { data: fileChanges } = await supabase
    .from('theme_file_changes')
    .select('*')
    .eq('schedule_id', id);

  const changes: ThemeFileChange[] = (fileChanges || []).map(fc => ({
    filename: fc.filename,
    fileType: fc.file_type,
    currentContent: fc.current_content,
    newContent: fc.new_content,
    action: fc.action,
  }));

  return mapThemeScheduleFromDB(row, changes);
}

/**
 * Update a theme schedule status
 */
export async function updateThemeScheduleStatus(
  id: string,
  status: ScheduleStatus,
  errorMessage?: string
): Promise<void> {
  const updateData: Record<string, any> = { status };
  
  if (status === 'completed' || status === 'failed') {
    updateData.processed_at = new Date().toISOString();
  }
  if (errorMessage) {
    updateData.error_message = errorMessage;
  }

  const { error } = await supabase
    .from('scheduled_theme_changes')
    .update(updateData)
    .eq('id', id);

  if (error) throw new Error(`Failed to update theme schedule: ${error.message}`);
}

/**
 * Cancel a theme schedule
 */
export async function cancelThemeSchedule(id: string): Promise<void> {
  const { error } = await supabase
    .from('scheduled_theme_changes')
    .update({ status: 'cancelled' })
    .eq('id', id);

  if (error) throw new Error(`Failed to cancel theme schedule: ${error.message}`);

  await logActivity('schedule_cancelled', 'theme', id);
}

/**
 * Delete a theme schedule and its file changes
 */
export async function deleteThemeSchedule(id: string): Promise<void> {
  // File changes will be cascade deleted due to foreign key
  const { error } = await supabase
    .from('scheduled_theme_changes')
    .delete()
    .eq('id', id);

  if (error) throw new Error(`Failed to delete theme schedule: ${error.message}`);
}

/**
 * Get pending theme schedules that are due for execution
 */
export async function getDueThemeSchedules(): Promise<ScheduledThemeChange[]> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('scheduled_theme_changes')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .eq('status', 'pending')
    .lte('scheduled_at', now)
    .order('scheduled_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch due theme schedules: ${error.message}`);

  // Fetch file changes for each
  const schedules = await Promise.all(
    (data || []).map(async (row) => {
      const { data: fileChanges } = await supabase
        .from('theme_file_changes')
        .select('*')
        .eq('schedule_id', row.id);

      const changes: ThemeFileChange[] = (fileChanges || []).map(fc => ({
        filename: fc.filename,
        fileType: fc.file_type,
        currentContent: fc.current_content,
        newContent: fc.new_content,
        action: fc.action,
      }));

      return mapThemeScheduleFromDB(row, changes);
    })
  );

  return schedules;
}

function mapThemeScheduleFromDB(row: any, changes: ThemeFileChange[]): ScheduledThemeChange {
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    targetMode: row.target_mode,
    targetThemeIds: row.target_theme_ids,
    executedOnThemes: row.executed_on_themes,
    changes,
    title: row.title,
    description: row.description,
    scheduledAt: row.scheduled_at,
    timezone: row.timezone,
    status: row.status as ScheduleStatus,
    processedAt: row.processed_at,
    errorMessage: row.error_message,
    retryCount: row.retry_count,
    createdBy: row.created_by,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ============================================================================
// VISUAL SECTION SCHEDULES
// ============================================================================

interface CreateSectionScheduleData {
  templateName: string;
  templateType?: 'template' | 'section_group';
  targetMode: 'main' | 'specific' | 'all_unpublished' | 'main_and_unpublished';
  targetThemeIds?: string[];
  changes: ScheduledSectionChange[];
  collectionTemplateAssignments?: CollectionTemplateAssignmentData[];  // For collection template scheduling
  title: string;
  description?: string;
  scheduledAt: string;
  scheduledOffAt?: string;  // When to revert the changes
  timezone: string;
  createdBy?: string;
  notes?: string;
}

export interface SectionScheduleConflict {
  scheduleId: string;
  title: string;
  scheduledAt: string;
  scheduledOffAt?: string | null;
  target: string;
  reason: string;
}

async function getFunctionErrorMessage(error: any, fallback: string): Promise<string> {
  const context = error?.context;
  if (context && typeof context.json === 'function') {
    try {
      const body = await context.json();
      return body?.message || body?.error || body?.details || fallback;
    } catch {
      // ignore
    }
  }
  return error?.message || fallback;
}

export async function checkSectionScheduleConflicts(
  data: CreateSectionScheduleData
): Promise<SectionScheduleConflict[]> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'checkSectionScheduleConflicts',
      data,
    },
  });

  if (error) {
    throw new Error(await getFunctionErrorMessage(error, 'Failed to check schedule conflicts'));
  }

  return (result?.conflicts || []) as SectionScheduleConflict[];
}

/**
 * Create a visual section schedule with optional revert schedule
 */
export async function createSectionSchedule(
  data: CreateSectionScheduleData
): Promise<VisualSectionSchedule> {
  console.log('[Schedule API] createSectionSchedule called with data:', {
    templateName: data.templateName,
    templateNameType: typeof data.templateName,
    templateNameLength: data.templateName?.length,
    containsComma: data.templateName?.includes(','),
    splitTest: data.templateName?.split(','),
    changesCount: data.changes.length,
    collectionAssignmentsCount: data.collectionTemplateAssignments?.length || 0,
    hasCollectionAssignments: !!data.collectionTemplateAssignments,
  });

  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'createSectionSchedule',
      data,
    },
  });

  if (error) throw new Error(await getFunctionErrorMessage(error, 'Failed to create section schedule'));
  if (!result?.schedule) throw new Error('Failed to create section schedule: missing response');

  return result.schedule as VisualSectionSchedule;
}

/**
 * Get all visual section schedules with optional filters
 * Uses batch fetching to avoid N+1 query problem
 */
export async function getSectionSchedules(
  filters?: FilterState
): Promise<VisualSectionSchedule[]> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'getSectionSchedules',
      data: { filters },
    },
  });

  if (error) throw new Error(`Failed to fetch section schedules: ${error.message}`);
  return (result?.schedules || []) as VisualSectionSchedule[];
}

/**
 * Get a single visual section schedule by ID
 */
export async function getSectionSchedule(id: string): Promise<VisualSectionSchedule | null> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'getSectionSchedule',
      data: { id },
    },
  });

  if (error) throw new Error(`Failed to fetch section schedule: ${error.message}`);
  return (result?.schedule || null) as VisualSectionSchedule | null;
}

/**
 * Cancel a visual section schedule
 */
export async function cancelSectionSchedule(id: string): Promise<void> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'cancelSectionSchedule',
      data: { id },
    },
  });

  if (error) throw new Error(`Failed to cancel section schedule: ${error.message}`);
}

/**
 * Update a visual section schedule status
 */
export async function updateSectionScheduleStatus(
  id: string,
  status: ScheduleStatus,
  errorMessage?: string
): Promise<void> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'updateSectionScheduleStatus',
      data: { id, status, errorMessage },
    },
  });

  if (error) throw new Error(`Failed to update section schedule status: ${error.message}`);
}

/**
 * Update a visual section schedule (for editing)
 */
export async function updateSectionSchedule(
  id: string,
  updates: {
    title?: string;
    description?: string;
    scheduledAt?: string;
    timezone?: string;
    status?: string;
  }
): Promise<VisualSectionSchedule> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'updateSectionSchedule',
      data: { id, updates },
    },
  });

  if (error) throw new Error(`Failed to update section schedule: ${error.message}`);
  if (!result?.schedule) throw new Error('Failed to update section schedule: missing response');
  return result.schedule as VisualSectionSchedule;
}

export async function updateAssetSwapReplacement(
  scheduleId: string,
  changeId: string,
  replacement: {
    value: string;
    label?: string;
    previewUrl?: string;
    fileId?: string;
  }
): Promise<VisualSectionSchedule> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'updateAssetSwapReplacement',
      data: { scheduleId, changeId, replacement },
    },
  });

  if (error) throw new Error(await getFunctionErrorMessage(error, 'Failed to update replacement asset'));
  if (!result?.schedule) throw new Error('Failed to update replacement asset: missing response');
  return result.schedule as VisualSectionSchedule;
}

/**
 * Delete a visual section schedule
 */
export async function deleteSectionSchedule(id: string): Promise<void> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'deleteSectionSchedule',
      data: { id },
    },
  });

  if (error) throw new Error(`Failed to delete section schedule: ${error.message}`);
}

/**
 * Get pending visual section schedules that are due for execution
 */
export async function getDueSectionSchedules(): Promise<VisualSectionSchedule[]> {
  const shopDomain = getShopDomainForAPI();
  const sessionToken = await getSessionTokenForBackend();

  const { data: result, error } = await supabase.functions.invoke('app-api', {
    headers: {
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: {
      action: 'getDueSectionSchedules',
      data: {},
    },
  });

  if (error) throw new Error(`Failed to fetch due section schedules: ${error.message}`);
  return (result?.schedules || []) as VisualSectionSchedule[];
}

// ============================================================================
// COLLECTION SWAP SCHEDULES
// ============================================================================

export interface CollectionSwapSchedule {
  id: string;
  shopDomain: string;
  sectionId: string;
  sectionType: string;
  sectionName: string;
  templateName: string;
  themeId: string;
  originalCollectionId?: string;
  originalCollectionTitle?: string;
  swapCollectionIds: string[];
  swapCollectionTitles: string[];
  startAt: string;
  endAt: string;
  timezone: string;
  status: ScheduleStatus;
  processedAt?: string;
  revertedAt?: string;
  errorMessage?: string;
  createdBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create a collection swap schedule
 * This schedules a temporary collection replacement for a section
 */
export async function createCollectionSwapSchedule(data: {
  sectionId: string;
  sectionType: string;
  sectionName: string;
  templateName: string;
  themeId: string;
  originalCollectionId?: string;
  originalCollectionTitle?: string;
  swapCollectionIds: string[];
  swapCollectionTitles: string[];
  startAt: string;
  endAt: string;
  timezone: string;
  notes?: string;
}): Promise<CollectionSwapSchedule> {
  const { data: result, error } = await supabase
    .from('collection_swap_schedules')
    .insert({
      shop_domain: getShopDomainForAPI(),
      section_id: data.sectionId,
      section_type: data.sectionType,
      section_name: data.sectionName,
      template_name: data.templateName,
      theme_id: data.themeId,
      original_collection_id: data.originalCollectionId,
      original_collection_title: data.originalCollectionTitle,
      swap_collection_ids: data.swapCollectionIds,
      swap_collection_titles: data.swapCollectionTitles,
      start_at: data.startAt,
      end_at: data.endAt,
      timezone: data.timezone,
      notes: data.notes,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create collection swap schedule: ${error.message}`);

  await logActivity('collection_swap_created', 'collection_swap', result.id, {
    section_name: data.sectionName,
    swap_collections: data.swapCollectionTitles,
    start_at: data.startAt,
    end_at: data.endAt,
  });

  return mapCollectionSwapFromDB(result);
}

/**
 * Get all collection swap schedules
 */
export async function getCollectionSwapSchedules(status?: ScheduleStatus): Promise<CollectionSwapSchedule[]> {
  let query = supabase
    .from('collection_swap_schedules')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .order('start_at', { ascending: true });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) throw new Error(`Failed to fetch collection swap schedules: ${error.message}`);
  return (data || []).map(mapCollectionSwapFromDB);
}

/**
 * Cancel a collection swap schedule
 */
export async function cancelCollectionSwapSchedule(id: string): Promise<void> {
  const { error } = await supabase
    .from('collection_swap_schedules')
    .update({ status: 'cancelled' })
    .eq('id', id);

  if (error) throw new Error(`Failed to cancel collection swap schedule: ${error.message}`);

  await logActivity('collection_swap_cancelled', 'collection_swap', id);
}

/**
 * Get due collection swap schedules for applying
 */
export async function getDueCollectionSwapSchedules(): Promise<CollectionSwapSchedule[]> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('collection_swap_schedules')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .eq('status', 'pending')
    .lte('start_at', now)
    .order('start_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch due collection swap schedules: ${error.message}`);
  return (data || []).map(mapCollectionSwapFromDB);
}

/**
 * Get collection swaps that need to be reverted (past end_at)
 */
export async function getExpiredCollectionSwapSchedules(): Promise<CollectionSwapSchedule[]> {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from('collection_swap_schedules')
    .select('*')
    .eq('shop_domain', getShopDomainForAPI())
    .eq('status', 'active')
    .lte('end_at', now)
    .order('end_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch expired collection swap schedules: ${error.message}`);
  return (data || []).map(mapCollectionSwapFromDB);
}

/**
 * Mark a collection swap as active (after applying)
 */
export async function markCollectionSwapActive(id: string): Promise<void> {
  const { error } = await supabase
    .from('collection_swap_schedules')
    .update({ status: 'active', processed_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw new Error(`Failed to mark collection swap active: ${error.message}`);
}

/**
 * Mark a collection swap as completed (after reverting)
 */
export async function markCollectionSwapCompleted(id: string): Promise<void> {
  const { error } = await supabase
    .from('collection_swap_schedules')
    .update({ status: 'completed', reverted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw new Error(`Failed to mark collection swap completed: ${error.message}`);
}

function mapCollectionSwapFromDB(row: any): CollectionSwapSchedule {
  return {
    id: row.id,
    shopDomain: row.shop_domain,
    sectionId: row.section_id,
    sectionType: row.section_type,
    sectionName: row.section_name,
    templateName: row.template_name,
    themeId: row.theme_id,
    originalCollectionId: row.original_collection_id,
    originalCollectionTitle: row.original_collection_title,
    swapCollectionIds: row.swap_collection_ids || [],
    swapCollectionTitles: row.swap_collection_titles || [],
    startAt: row.start_at,
    endAt: row.end_at,
    timezone: row.timezone,
    status: row.status as ScheduleStatus,
    processedAt: row.processed_at,
    revertedAt: row.reverted_at,
    errorMessage: row.error_message,
    createdBy: row.created_by,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
